import { LocalStorageService } from '../local-storage/local-storage.service';
import { SuppliesUtils as Supplies } from '../../utils/supplies.utils';
import { EXTEND_FILES, MULTIPLEX_PROJECTS, SELECTED_PROJECT } from '../../constants/localstorage';
import { Project, ImportProject } from '../../interfaces/project';
import { Injectable } from "@angular/core";
import { ProjectClass } from '../../models/project.model';
import { MAX_NUMBER_PROJECTS_LIN, TYPE_FILE } from '../../constants/project';
import { NotificationService } from '../notification/notification.service';
import * as moment from 'moment';
import { HttpClient } from '@angular/common/http';
import { swalOldProjectAlert } from 'src/app/utils/swals.utils';
import { TranslateService } from '@ngx-translate/core';


@Injectable({
  providedIn: "root",
})
export class ProjectsService {

  public SelectedProject: Project;
  private devices: any[] = [];

  constructor(
    private _localStorage: LocalStorageService,
    private _notifications: NotificationService,
    private _http: HttpClient,
    private _translate: TranslateService
  ) {
    // cargar devices.json al iniciar
    this._http.get<any[]>("assets/json/devices.json").subscribe(data => {
      this.devices = data;
    });
  }

  /**
   * Obtiene la lista de proyectos desde el LocalStorage
   * @returns {Array<Project>} Lista de Proyectos
   */
  public get getListProjects(): Array<Project> {
    const Projects: Array<Project> = this._localStorage.getItem(MULTIPLEX_PROJECTS) ?? [];
    return Projects.sort((a, b) => new Date(b.creation_date).getTime() - new Date(a.creation_date).getTime());
  }

  /**
   * Obtiene el proyecto seleccionado desde el LocalStorage.
   * @returns {Project} Proyecto Seleccionado
   */
  public get getSelectedProject(): Project {
    const SelectedProject: Project = this._localStorage.getItem(SELECTED_PROJECT) ?? null
    this.SelectedProject = SelectedProject;
    return SelectedProject;
  }

  /**
   * Obtiene el tamaño total de la memoria del proyecto.
   * @returns {number}
   */
  public get getMemorySize(): number {
    const { memory_size: MemorySize } = this.getSelectedProject;
    return MemorySize;
  }

  /**
   * Guarda en LocalStorage la lista de proyectos actual.
   * @param {Array<Project>} projects - Lista de Proyectos
   */
  public set setListProject(projects: Array<Project>) {
    this._localStorage.setItem<Project[]>(MULTIPLEX_PROJECTS, projects);
  }

  /**
   * Guarda en LocalStorage el proyecto Seleccionado
   * @param {Project} project - Proyecto Seleccionado
   */
  public set setProjectSelected(project: Project) {
    this.SelectedProject = project;
    this._localStorage.setItem<Project>(SELECTED_PROJECT, project);
  }

  /**
   * Crear el proyecto y actualiza el LocalStorage.
   * @param {any} project_data - Información del proyecto.
   * @returns {Promise<Project[]>} Lista de Proyectos actualizada.
   */
  public createProject(project_data: any): Promise<Project[]> {
    return new Promise<Project[]>((resolve) => {
      const ProjectList: Array<Project> = this.getListProjects;
      const ProjectID: string = Supplies.generateUUID;
      const NewProject: Project = new ProjectClass({ ProjectID, ...project_data });
      ProjectList.unshift(NewProject);
      this.setListProject = ProjectList;
      resolve(ProjectList);
    });
  }

  /**
   * Actualiza el nombre, protocolo, autor, versión y fecha de mofidicación del proyecto
   * @param {any} param - { Nombre Proyecto, Protocolo, Autor, Version }
   * @param {Project} project - Proyecto a Actualizar
   * @returns { void }
   */
  public updateProject({ name, protocol, author, version, bodywork }: any, project: Project): Promise<Project[]> {
    return new Promise<Project[]>((resolve) => {
      const Condition: boolean = (project.network.length > MAX_NUMBER_PROJECTS_LIN && protocol === 'LIN');
      if (!Condition) {
        const ProjectList: Array<Project> = this.getListProjects;
        const SelectedProject: Project = this.getSelectedProject;
        const Index: number = ProjectList.findIndex(({ id }: Project) => id === project.id);
        const Project: Project = { ...project, name, protocol, bodywork, author, version, modification_date: moment().format('DD-MM-YYYY hh:mm:ss') };
        if (SelectedProject !== null && (SelectedProject.id === Project.id)) this.setProjectSelected = Project;
        ProjectList[Index] = Project;
        this.setListProject = ProjectList;
        this._notifications.showNotification('Project_admin.MESSAGES.PROJECT_UPDATED', 'success', 'ui-1_check');
        resolve(ProjectList);
      } else {
        this._notifications.showNotification('Project_admin.ERRORS.LIN_NETWORK', 'danger', 'ui-1_simple-remove');
        resolve([]);
      }
    });
  }

  /**
   * Duplica el proyecto dado y lo guarda en LocalStorage
   * @param {Project} project - Proyecto a duplicar.
   * @returns {Promise<Project[]>} Lista de Proyectos actualizada.
   */
  public duplicateProject(project: Project): Promise<Project[]> {
    return new Promise<Project[]>((resolve) => {
      const ProjectList: Array<Project> = this.getListProjects;
      const Project: Project = {
        ...project,
        id: Supplies.generateUUID,
        name: Supplies.generateNewName(ProjectList, project.name),
        modification_date: project.modification_date,
        //creation_date: moment().format('DD-MM-YYYY hh:mm:ss')
      };
      ProjectList.unshift(Project);
      this.setListProject = ProjectList;
      this._notifications.showNotification('Project_admin.MESSAGES.PROJECT_DUPLICATED', 'success', 'ui-1_check');
      resolve(ProjectList);
    });
  }

  /**
   * Elimina un proyecto de la lista de proyectos.
   * @param {Project} project - Proyecto a eliminar.
   * @returns {Promise<Project[]>}  Lista de Proyectos actualizada.
   */
  public deleteProject(project: Project): Promise<Project[]> {
    return new Promise<Project[]>((resolve) => {
      const ProjectList: Array<Project> = this.getListProjects;
      const SelectedProject: Project = this.getSelectedProject;
      const ProjectIndex: number = ProjectList.findIndex(({ id }: Project) => project.id === id);
      ProjectList.splice(ProjectIndex, 1);
      if (SelectedProject !== null && (SelectedProject.id === project.id)) {
        this.SelectedProject = null;
        this._localStorage.removeItem(SELECTED_PROJECT);
      }
      this.setListProject = ProjectList;
      resolve(ProjectList);
    });
  }

  /**
   * Exporta el proyecto dado
   * @param {Project} project - Proyecto a exportar.
   * @returns {Promise<void>}
   */
  public async exportFile(project: Project): Promise<void> {
    const Project: string = JSON.stringify({
      type: TYPE_FILE,
      import: "Project",
      data: project
    });
    const Date: any = moment().format('x');
    const Response: boolean = await Supplies.exportFile({
      content: Project,
      file_name: `${project.name}-${project.author}-V${project.version} ${Date}${EXTEND_FILES}`
    });
    if (Response) this._notifications.showNotification('Project_admin.MESSAGES.EXPORTED_PROJECT', 'success', 'ui-1_check');
  }

  /**
   * Importa un proyecto dado
   * @param {any} file - Archivo a Importar
   * @returns {Promise<Project>} Proyecto Importado.
   */
  public async importProject(file: any): Promise<Project> {
    const { status, content, message }: ImportProject = await Supplies.importFile(file);
    if (status) {
      if (content) {
        const { type, data, import: Import }: any = JSON.parse(content);
        if (type !== TYPE_FILE || Import !== 'Project') {
          if (type !== TYPE_FILE) {
            this._notifications.showNotification('Project_admin.ERRORS.TYPE_INCORRECT', 'danger', 'ui-1_simple-remove');
          } else this._notifications.showNotification('Project_admin.ERRORS.IMPORT_INCORRECT', 'warning', 'travel_info');
        } else return data;
      }
    } else this._notifications.showNotification(message, 'danger', 'ui-1_simple-remove');
  }

  public updateSelectedProjectModificationDate(): void {
  if (!this.SelectedProject) return;

  const projectList: Array<Project> = this.getListProjects;
  const index: number = projectList.findIndex(p => p.id === this.SelectedProject.id);

  if (index !== -1) {
    const updatedProject: Project = {
      ...this.SelectedProject,
      modification_date: moment().format('DD-MM-YYYY hh:mm:ss')
    };

    projectList[index] = updatedProject;

    this.setListProject = projectList;
    this.setProjectSelected = updatedProject;
    }
  }


  /**
   * Actualiza los campos del proyecto seleccionado.
   * @param {any} params - Parámetros a guardar.
   * @returns {void}
   */
  public updateSelectedProject(params: any): void {
    let project_list: Array<Project> = this.getListProjects;
    const { signs, structure, memory_size, devices } = params;
    const SelectedProject: Project = {
      ...this.getSelectedProject,
      network: devices,
      memory_size: memory_size,
      logical: { blocks: { signs }, structure },
      modification_date: moment().format("DD-MM-YYYY hh:mm:ss")
    };
    project_list = project_list.map((project: Project) => {
      return (project.id === SelectedProject.id ? { ...SelectedProject } : { ...project });
    });
    this.setListProject = project_list;
    this.setProjectSelected = SelectedProject;
  }
}